/**
 * Rule Build Page
 *
 * Main page for the Rule Build feature. Provides a task-based workflow
 * for creating, editing, and managing compliance rules through AI chat.
 * Styled to match JotAudit design patterns.
 *
 * Layout:
 * - Left: Draft work area (list + editor)
 * - Right: AI chat panel
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Build as BuildIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as GeneratingIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useFeature } from '@store/features/hooks';
import type { DraftRuleResponse, TaskResponse } from '@store/generated/rule-build';
import { useAppDispatch } from '@store/hooks';

import { DraftEditor } from '../components/DraftEditor';
import { DraftList } from '../components/DraftList';
import { EmptyState } from '../components/EmptyState';
import { RuleBuildChat } from '../components/RuleBuildChat';
import { CHAT_SIDEBAR_WIDTH, TASK_STATUS_LABELS } from '../constants';
import { useChatActions, useCreateTask,useTaskPolling } from '../hooks';
import {
  initializeChat,
  ruleBuildFeature,
  setFocusedDraftId,
} from '../store';

/**
 * Get status display info
 */
const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'completed':
      return { icon: CompletedIcon, color: '#22C55E', bgColor: '#22C55E15' };
    case 'failed':
      return { icon: FailedIcon, color: '#EF4444', bgColor: '#EF444415' };
    case 'generating':
      return { icon: GeneratingIcon, color: '#F59E0B', bgColor: '#F59E0B15' };
    case 'active':
    case 'ready':
      return { icon: BuildIcon, color: '#2196F3', bgColor: '#2196F315' };
    default:
      return { icon: BuildIcon, color: '#6B7280', bgColor: '#6B728015' };
  }
};

/**
 * Main Rule Build Page Component
 */
export const RuleBuildPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId?: string }>();
  const { ruleBuild } = useEventTracking();

  // Tracking refs
  const pageViewedRef = useRef<string | null>(null);
  const generationStartedRef = useRef<string | null>(null);

  // Ensure feature is registered
  const isFeatureReady = useFeature(ruleBuildFeature);

  // Task polling for async generation
  const {
    task,
    status: taskStatus,
    isLoading: isTaskLoading,
    isPolling,
    hasTimedOut,
    hasFailed,
    error: pollingError,
    refetch,
  } = useTaskPolling(taskId || null, {
    onComplete: (completedTask) => {
      console.debug('📋 Task generation complete:', completedTask.id);
      ruleBuild.generationCompleted(completedTask.id, {
        draft_count: (completedTask).draft_count || 0,
      });
    },
    onFailed: (failedTask) => {
      console.error('❌ Task generation failed:', failedTask.id);
      ruleBuild.generationFailed(failedTask.id);
    },
    onTimeout: () => {
      console.warn('⏱️ Task polling timed out');
      if (taskId) {
        ruleBuild.generationTimeout(taskId);
      }
    },
    enabled: Boolean(taskId),
  });

  // Refetch callback for keeping state in sync
  const handleRefetch = useCallback(() => {
    console.log('[RuleBuildPage] Refetching task status');
    refetch();
  }, [refetch]);

  // Chat actions with stream end callback
  const chatActions = useChatActions(
    taskId || null,
    isFeatureReady && Boolean(taskId),
    {
      onStreamEnd: handleRefetch,
    },
  );

  // Task creation for new tasks
  const { createEmptyTask, isCreating } = useCreateTask();

  // Selected draft state
  const [selectedDraftId, setSelectedDraftId] = React.useState<string | null>(null);

  // Memoized drafts from task
  const drafts = useMemo(() => {
    return (task as TaskResponse)?.draft_rules || [];
  }, [task]);

  // Selected draft
  const selectedDraft = useMemo(() => {
    if (!selectedDraftId) return null;
    return drafts.find((d: DraftRuleResponse) => d.id === selectedDraftId) || null;
  }, [drafts, selectedDraftId]);

  // Initialize chat when task changes
  useEffect(() => {
    if (isFeatureReady && taskId) {
      dispatch(initializeChat({ taskId }));
    }
  }, [isFeatureReady, taskId, dispatch]);

  // Track page view
  useEffect(() => {
    if (isFeatureReady && taskId && pageViewedRef.current !== taskId) {
      pageViewedRef.current = taskId;
      ruleBuild.pageViewed({
        task_id: taskId,
        has_drafts: drafts.length > 0,
      });
    }
  }, [isFeatureReady, taskId, drafts.length, ruleBuild]);

  // Track generation started when polling begins
  useEffect(() => {
    if (isPolling && taskId && generationStartedRef.current !== taskId) {
      generationStartedRef.current = taskId;
      ruleBuild.generationStarted(taskId, { source: 'page_load' });
    }
  }, [isPolling, taskId, ruleBuild]);

  // Select first draft when drafts load
  useEffect(() => {
    if (drafts.length > 0 && !selectedDraftId) {
      setSelectedDraftId(drafts[0].id);
    }
  }, [drafts, selectedDraftId]);

  // Update focused draft in chat context
  useEffect(() => {
    if (selectedDraftId) {
      dispatch(setFocusedDraftId(selectedDraftId));
    }
  }, [selectedDraftId, dispatch]);

  // Handle draft selection
  const handleDraftSelect = useCallback((draftId: string) => {
    setSelectedDraftId(draftId);
    if (taskId) {
      const draft = drafts.find((d: DraftRuleResponse) => d.id === draftId);
      ruleBuild.draftSelected(taskId, draftId, {
        draft_status: draft?.status,
      });
    }
  }, [taskId, drafts, ruleBuild]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/jotaudit');
  }, [navigate]);

  // Handle create new task
  const handleCreateTask = useCallback(async () => {
    try {
      const result = await createEmptyTask();
      navigate(`/rule-build/${result.taskId}`);
    } catch (err) {
      console.error('Failed to create task:', err);
    }
  }, [createEmptyTask, navigate]);

  // Status display
  const statusDisplay = taskStatus ? getStatusDisplay(taskStatus) : null;
  const StatusIcon = statusDisplay?.icon;

  // Render loading state
  if (!isFeatureReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 0,
          minWidth: 0,
          bgcolor: theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render empty state (no task selected)
  if (!taskId) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 0,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              color: 'textColors.lightText',
              '&:hover': {
                bgcolor: theme.palette.grey[100],
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: `${theme.palette.primary.main}15`,
              color: theme.palette.primary.main,
            }}
          >
            <BuildIcon sx={{ fontSize: 18 }} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'textColors.darkText',
            }}
          >
            Rule Builder
          </Typography>
        </Paper>

        {/* Empty state content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          <EmptyState onCreateTask={handleCreateTask} isCreating={isCreating} />
        </Box>
      </Box>
    );
  }

  // Render task view
  return (
    <Box
      id="rule-build-workspace"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        height: '100%',
          minHeight: 0,
          minWidth: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Main content area */}
      <Box
        sx={{
          flex: { xs: '1 0 760px', sm: '1 0 440px', lg: '1 1 0' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 0,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              color: 'textColors.lightText',
              '&:hover': {
                bgcolor: theme.palette.grey[100],
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: `${theme.palette.primary.main}15`,
              color: theme.palette.primary.main,
            }}
          >
            <BuildIcon sx={{ fontSize: 18 }} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'textColors.darkText',
            }}
          >
            Rule Builder
          </Typography>

          {/* Status badge */}
          {taskStatus && statusDisplay && StatusIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: statusDisplay.bgColor,
              }}
            >
              {taskStatus === 'generating' ? (
                <CircularProgress size={12} thickness={4} sx={{ color: statusDisplay.color }} />
              ) : (
                <StatusIcon sx={{ fontSize: 14, color: statusDisplay.color }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: statusDisplay.color,
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                }}
              >
                {TASK_STATUS_LABELS[taskStatus as keyof typeof TASK_STATUS_LABELS] || taskStatus}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Error state */}
          {pollingError ? (
            <Box sx={{ p: 3, width: '100%', overflow: 'auto' }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {pollingError instanceof Error ? pollingError.message : 'An error occurred'}
              </Alert>
            </Box>
          ) : null}

          {/* Timeout state */}
          {hasTimedOut && (
            <Box sx={{ p: 3, width: '100%', overflow: 'auto' }}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Task generation is taking longer than expected. Please try again.
              </Alert>
            </Box>
          )}

          {/* Failed state */}
          {hasFailed && (
            <Box sx={{ p: 3, width: '100%', overflow: 'auto' }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Rule generation failed. Please try again or create a new task.
              </Alert>
            </Box>
          )}

          {/* Loading state */}
          {isTaskLoading && !task && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography sx={{ color: 'textColors.lightText' }}>
                Loading task...
              </Typography>
            </Box>
          )}

          {/* Generating state */}
          {isPolling && !drafts.length && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: `${theme.palette.primary.main}10`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: 'textColors.darkText',
                }}
              >
                Generating rules...
              </Typography>
              <Typography sx={{ color: 'textColors.lightText' }}>
                This may take a few moments
              </Typography>
            </Box>
          )}

          {/* Draft work area */}
          {task && !isPolling && !pollingError && !hasTimedOut && !hasFailed && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
              {/* Draft list */}
              <Box
                sx={{
                  width: { xs: '100%', sm: 280, lg: 320 },
                  flexBasis: { xs: 240, sm: 'auto' },
                  minHeight: 0,
                  minWidth: 0,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <DraftList
                  drafts={drafts}
                  selectedDraftId={selectedDraftId}
                  onSelectDraft={handleDraftSelect}
                  taskId={taskId}
                />
              </Box>

              {/* Draft editor */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  minWidth: 0,
                }}
              >
                {selectedDraft ? (
                  <DraftEditor
                    draft={selectedDraft}
                    taskId={taskId}
                    onRefetch={handleRefetch}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: theme.palette.grey[100],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <BuildIcon sx={{ fontSize: 24, color: 'textColors.lightText' }} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: 'textColors.darkText',
                        mb: 0.5,
                      }}
                    >
                      {drafts.length > 0
                        ? 'Select a draft to edit'
                        : 'No drafts yet'}
                    </Typography>
                    <Typography sx={{ color: 'textColors.lightText' }}>
                      {drafts.length > 0
                        ? 'Choose a draft from the list to start editing'
                        : 'Use the chat to create rules'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Chat sidebar */}
      <Box
        id="rule-build-chat"
        sx={{
          width: { xs: '100%', lg: CHAT_SIDEBAR_WIDTH },
          flexBasis: { xs: 360, lg: 'auto' },
          minHeight: 0,
          minWidth: 0,
          flexShrink: 0,
          borderLeft: `1px solid ${theme.palette.grey[200]}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <RuleBuildChat
          taskId={taskId}
          chatActions={chatActions}
        />
      </Box>
    </Box>
  );
};

export default RuleBuildPage;
